/*
    useAnalytics
    ---
    Provides Firebase analytics features
    https://firebase.google.com/docs/reference/js/analytics
*/

import { getAnalytics, getGoogleAnalyticsClientId, setUserId, setUserProperties, logEvent } from 'firebase/analytics';
import { useFirebase } from './useFirebase';
import { useProduct } from './useProduct';
import { useStoreProduct } from './useStoreProduct';
import { LayerGroup, MapProvider, MapStyle, ResultType } from './useConstants';
import { CoordinateFormatKey, CollectionType } from 'ihunter-common';

const UserProperty = Object.freeze({
    AppRegion: 'app_region',
    Basemap: 'basemap',
    ChatSize: 'chat_size',
    CollectionSize: 'collection_size',
    CoordinateFormat: 'coordinate_format',
    HasHiddenItems: 'has_hidden_items',
    IsEmailVerified: 'is_email_verified',
    IsFreeUser: 'is_free_user',
    IsNewAccount: 'is_new_account',
    Language: 'language',
    SubscriptionTier: 'subscription_tier',
});

// Consider the account to be new if it was created in the last 24 hours
const NEW_ACCOUNT_CREATED_HOURS = 24;

const CollectionSizeRange = Object.freeze({
    xs: { max: 0 },
    small: { min: 1, max: 9 },
    medium: { min: 10, max: 99 },
    large: { min: 100, max: 499 },
    xl: { min: 500, max: 999 },
    xxl: { min: 1000 },
});

const ChatSizeRange = Object.freeze({
    xs: { max: 0 },
    small: { min: 1, max: 2 },
    medium: { min: 3, max: 4 },
    large: { min: 5, max: 9 },
    xl: { min: 10, max: 19 },
    xxl: { min: 20 },
});

const EventType = Object.freeze({
    AddToCart: 'add_to_cart',
    AgLeaseEmailed: 'aglease_emailed',
    AgLeaseViewed: 'aglease_viewed',
    AppRegionChanged: 'app_region_changed',
    AuthAccountCreated: 'auth_account_created',
    AuthLinkRequested: 'auth_link_requested',
    AuthPasswordReset: 'auth_password_reset',
    AuthSignedIn: 'auth_signed_in',
    AuthSignedOut: 'auth_signed_out',
    BasemapModeChanged: 'basemap_mode_changed',
    BasemapSwitched: 'basemap_switched',
    BeginCheckout: 'begin_checkout',
    BoundaryLocationJumpedTo: 'boundary_location_jumped_to',
    ChatCleared: 'chat_cleared',
    ChatDrawerViewed: 'chat_drawer_viewed',
    ChatLeft: 'chat_left',
    ChatMessageSent: 'chat_message_sent',
    ChatSelected: 'chat_selected',
    CollectionFilterEdited: 'collection_filter_edited',
    CollectionFilterEditStarted: 'collection_filter_edit_started',
    ContactGroupCreated: 'contact_group_created',
    ContactGroupDeleted: 'contact_group_deleted',
    ContactGroupEdited: 'contact_group_edited',
    ContactGroupSettingsViewed: 'contact_group_settings_viewed',
    ContactLocationViewed: 'contact_location_viewed',
    InviteRequestAccepted: 'invite_request_accepted',
    InviteRequestRejected: 'invite_request_rejected',
    InviteRequestRevoked: 'invite_request_revoked',
    InviteRequestSent: 'invite_request_sent',
    ItemCreated: 'item_created',
    ItemCreateStarted: 'item_create_started',
    ItemDeleted: 'item_deleted',
    ItemDeleteStarted: 'item_delete_started',
    ItemEdited: 'item_edited',
    ItemEditStarted: 'item_edit_started',
    ItemLocationJumpedTo: 'item_location_jumped_to',
    ItemReceived: 'item_received',
    ItemReceiveStarted: 'item_receive_started',
    ItemSelected: 'item_selected',
    ItemShared: 'item_shared',
    ItemShareStarted: 'item_share_started',
    LandownerMapSelected: 'landowner_map_selected',
    LayerSelected: 'layer_selected',
    LayerBoundarySelected: 'layer_boundary_selected',
    NavigatedTo: 'navigated_to',
    RemoveFromCart: 'remove_from_cart',
    ResourceLinkOpened: 'resource_link_opened',
    ResourcePreviewed: 'resource_previewed',
    Search: 'search',
    SearchResultSelected: 'search_result_selected',
    ScreenView: 'screen_view',
    ShopInStoreClicked: 'shop_in_store_clicked',
    SupportEmailed: 'support_emailed',
    ViewCart: 'view_cart',
    ViewItem: 'view_item',
});

const EventParam = Object.freeze({
    AuthType: 'auth_type',
    ChatType: 'chat_type',
    ContentType: 'content_type',
    HasCustomName: 'has_custom_name',
    HasCustomImage: 'has_custom_image',
    InviteCount: 'invite_count',
    IsBoundaryVisible: 'is_boundary_visible',
    IsResent: 'is_resent',
    IsVisible: 'is_visible',
    CoordinateFormat: 'coordinate_format',
    CoordinateLocation: 'coordinate_location',
    ItemCount: 'item_count',
    ItemPinColor: 'item_pin_color',
    ItemPinOveray: 'item_pin_overlay',
    ItemType: 'item_type',
    LayerGroup: 'layer_group',
    MapName: 'map_name',
    MapType: 'map_type',
    Method: 'method',
    Mode: 'mode',
    ParticipantCount: 'participant_count',
    PurchaseStatus: 'purchase_status',
    ScreenName: 'screen_name',
    ResourceType: 'resource_type',
    ResultType: 'result_type',
    ResultSubtype: 'result_subtype',
    Role: 'role',
    Source: 'source',
    Status: 'status',
    Target: 'target',
});

// Default Firebase params that are automatically collected
// https://support.google.com/analytics/answer/9234069?hl=en
const FirebaseEventParam = Object.freeze({
    PreviousClass: 'firebase_previous_class',
    PreviousScreen: 'firebase_previous_screen',
    Class: 'firebase_screen_class',
    Screen: 'firebase_screen',
});

const EventSource = Object.freeze({
    BoundaryDetails: 'boundaryDetails',
    BrowseLayers: 'browseLayers',
    Chat: 'chat',
    Contacts: 'contacts',
    Drawer: 'drawer',
    EmailLink: 'emailLink',
    GlobalSearch: 'globalSearch',
    InApp: 'inApp',
    ItemDetails: 'itemDetails',
    ItemList: 'itemList',
    MapList: 'mapList',
    MapOverlay: 'mapOverlay',
    MapSelection: 'mapSelection',
    Search: 'search',
});

const EventLayerGroup = Object.freeze({
    BuiltInLayer: 'builtInLayer',
    LandownerMap: 'landownerMap',
    LEH: 'leh',
    Subscription: 'subscription',
    WMU: 'wmu',
});

const EventContentType = Object.freeze({
    Broadcast: 'broadcast',
    CurrentLocation: 'snapshot',
});

const EventMethod = Object.freeze({
    Apply: 'apply',
    Clipboard: 'clipboard',
    Refocus: 'refocus',
    Reset: 'reset',
    Sms: 'sms',
    Start: 'start',
    Stop: 'stop',
});

const EventStatus = Object.freeze({
    // item_receive_started: whether any received items are duplicates
    NoDuplicates: 'no_duplicates',
    SomeDuplicates: 'some_duplicates',
    AllDuplicates: 'all_duplicates',

    // item_received: how duplicates were resolved (NoDuplicates when there weren't any)
    DuplicatesAccepted: 'duplicates_accepted',
    DuplicatesSkipped: 'duplicates_skipped',

    // collection_filter_edited: whether the hidden colours need per-type colours, or could be hidden by whole types and colours
    HiddenTypeColours: 'type_colour', // a colour is hidden for some of the types that have it, but not all
    HiddenColours: 'colour', // a colour is hidden for every type that has it
    HiddenTypes: 'type', // only whole types are hidden
});

// collection_filter_edited: the date filter mode
const EventDateMode = Object.freeze({
    AllDates: 'all_dates',
    DateRange: 'date_range',
    PreviousDays: 'previous_days',
});

// collection_filter_*: content_type tags (in this order) for the parts of the filter that hide something, or 'none'
const EventFilterContent = Object.freeze({
    Date: 'date',
    Icon: 'icon',
    None: 'none',
});

let analytics, user, userUid, previousScreen, previousClass, dialogScreen, userBasemap, userHasHiddenItems;
let analyticsClientId, analyticsSessionId;
let pendingIdsPromise = null;
let pendingEvents = [];
let requestedAuthLinks = {};

export function useAnalytics() {
    const { firebaseApp } = useFirebase();
    const { product } = useProduct();

    /**
     * Initializes the Firebase Analytics instance if it hasn't been instantiated yet.
     *
     * As part of the initialization process, this function asynchronously pre-fetches
     * the Google Analytics Client ID in the background, so that it's available when needed.
     *
     * @returns {void}
     */
    const intializeAnalytics = () => {
        if (!analytics) {
            console.log(`Initializing analytics`);
            analytics = getAnalytics(firebaseApp);
            lookupAnalyticsIds();
        }
    };

    /**
     * Retrieves the Google Analytics Client ID and Session ID, for billing api calls.
     *
     * ### Approach:
     * - **Singleton State:** Resolved ids are cached globally to prevent network delays.
     * - **Concurrency Control:** Combines multiple simultaneous calls into a single lookup.
     * - **Firebase + gtag Fallback:** Uses native Firebase for the Client ID, but falls
     *   back to the underlying `gtag` API for the Session ID (as Firebase Web SDK
     *   does not natively export a session getter yet).
     * - **Ad-blocker Resilience:** Races the lookup against a timeout to guarantee
     *   the checkout flow continues even if scripts are blocked.
     *
     * @param {number} [timeout=2000] - Maximum milliseconds to wait before aborting
     * @returns {Promise<{analyticsClientId: string|null, analyticsSessionId: string|null}>}
     */
    const lookupAnalyticsIds = async (timeout = 2000) => {
        if (!analytics) {
            throw new Error('Must initialize analytics before getting client id');
        }
        if (analyticsClientId && analyticsSessionId) {
            return { analyticsClientId, analyticsSessionId };
        }
        if (pendingIdsPromise) {
            return pendingIdsPromise;
        }

        pendingIdsPromise = (async () => {
            let timeoutId;
            try {
                console.log(`Retrieving analytics client and session ids...`);
                const measurementId = analytics.app.options.measurementId;
                const clientPromise = getGoogleAnalyticsClientId(analytics);

                // Fetch Session ID via gtag (Firebase doesn't have a native getter for this)
                const sessionPromise = new Promise((resolve) => {
                    if (typeof window !== 'undefined' && typeof window.gtag === 'function' && measurementId) {
                        window.gtag('get', measurementId, 'session_id', (id) => resolve(id || null));
                    } else {
                        resolve(null);
                    }
                });

                // Use Promise.race to prevent hangs if privacy blockers block the gtag scripts
                const [fetchedClientId, fetchedSessionId] = await Promise.race([
                    Promise.all([clientPromise, sessionPromise]),
                    new Promise((resolve) => {
                        timeoutId = setTimeout(() => {
                            console.warn('Analytics id lookup timed out - privacy blocker may be active.');
                            resolve([null, null]);
                        }, timeout);
                    }),
                ]);

                analyticsClientId = fetchedClientId;
                analyticsSessionId = fetchedSessionId;

                console.log(`Retrieved analytics ids: client id=${analyticsClientId}, session id=${analyticsSessionId}`);
                return { analyticsClientId, analyticsSessionId };
            } catch (error) {
                console.warn('Failed to retrieve analytics ids', error.message);
                return null;
            } finally {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                pendingIdsPromise = null;
            }
        })();

        return pendingIdsPromise;
    };

    const setAnalyticsUser = (firebaseUser) => {
        try {
            intializeAnalytics();
            user = firebaseUser;
            if (userUid !== user?.uid) {
                userUid = user?.uid;
                setUserId(analytics, userUid);
                if (user) {
                    setAuthUserProperties();
                }
                userHasHiddenItems = undefined;
                requestedAuthLinks = {};
                console.log(`Set analytics user id ${userUid}`);
            }

            if (pendingEvents.length) {
                console.log(`Processing ${pendingEvents.length} queued events...`);
                pendingEvents.forEach((x) => x());
                pendingEvents = [];
            }
        } catch (error) {
            console.error(`setAnalyticsUser failed`, error);
        }
    };

    const setAuthUserProperties = () => {
        setAnalyticsUserProperty(UserProperty.IsEmailVerified, Boolean(user.emailVerified));

        const createdAt = parseInt(user.metadata?.createdAt);
        if (createdAt > 0) {
            const nowMs = Date.now();
            const minCreatedAtMs = nowMs - NEW_ACCOUNT_CREATED_HOURS * 60 * 60 * 1000;
            const isNewAccount = createdAt >= minCreatedAtMs;
            setAnalyticsUserProperty(UserProperty.IsNewAccount, isNewAccount);
        }
    };

    const setAnalyticsUserBasemap = (basemap) => {
        if (user && userBasemap !== basemap) {
            setAnalyticsUserProperty(UserProperty.Basemap, basemap);
            userBasemap = basemap;
        }
    };

    // Whether the collection filter hides any items, set at startup and whenever that changes
    const setAnalyticsUserHasHiddenItems = (hasHiddenItems) => {
        if (user && userHasHiddenItems !== hasHiddenItems) {
            setAnalyticsUserProperty(UserProperty.HasHiddenItems, hasHiddenItems);
            userHasHiddenItems = hasHiddenItems;
        }
    };

    const setAnalyticsIsFreeUser = (isFreeUser) => {
        setAnalyticsUserProperty(UserProperty.IsFreeUser, isFreeUser);
    };

    const setAnalyticsUserSubscriptionTier = (subscriptionTier) => {
        setAnalyticsUserProperty(UserProperty.SubscriptionTier, subscriptionTier);
    };

    const setAnalyticsUserCollectionSize = (size) => {
        console.debug(`setAnalyticsUserCollectionSize for ${userUid}: ${size}`);
        const sizeName = getSizeName(size, CollectionSizeRange);
        if (sizeName) {
            setAnalyticsUserProperty(UserProperty.CollectionSize, sizeName);
        }
    };

    const setAnalyticsUserChatSize = (size) => {
        console.debug(`setAnalyticsUserChatSize for ${userUid}: ${size}`);
        const sizeName = getSizeName(size, ChatSizeRange);
        if (sizeName) {
            setAnalyticsUserProperty(UserProperty.ChatSize, sizeName);
        }
    };

    const setAnalyticsUserLanguage = (language) => {
        setAnalyticsUserProperty(UserProperty.Language, language);
    };

    const setAnalyticsUserCoordinateFormat = (format) => {
        const coordinateFormat = getCoordinateFormat(format);
        if (coordinateFormat) {
            setAnalyticsUserProperty(UserProperty.CoordinateFormat, coordinateFormat);
        }
    };

    const setAnalyticsUserAppRegion = (region) => {
        setAnalyticsUserProperty(UserProperty.AppRegion, getAppRegion(region));
    };

    const setAnalyticsUserProperty = (propertyName, propertyValue) => {
        try {
            if (!analytics || !user || !userUid) {
                console.warn(`setAnalyticsUser must be called with an authenticated user before setAnalyticsUserProperty`);
            } else if (propertyName && propertyValue !== undefined) {
                const properties = sanitizeParams({ [propertyName]: propertyValue });
                setUserProperties(analytics, properties);
                console.log(`setAnalyticsUserProperty for ${userUid}`, properties);
            }
        } catch (error) {
            console.error(`setAnalyticsUserProperty failed for ${userUid}`, error);
        }
    };

    const getSizeName = (sizeValue, sizeRanges) => {
        if (typeof sizeValue === 'number' && !isNaN(sizeValue)) {
            for (const [sizeName, range] of Object.entries(sizeRanges)) {
                const min = range.min ?? -Infinity;
                const max = range.max ?? Infinity;
                if (sizeValue >= min && sizeValue <= max) {
                    return sizeName;
                }
            }
        }
        return null;
    };

    const getDefaultParams = (name, params) => {
        let allParams = { ...params };

        // ScreenName allows us to distinguish between the same events that are triggered from different screens (e.g. Login vs Accept Invite)
        // Also the built-in 'firebase_screen' dimension cannot be accessed in all scenarios (e.g. User Activity report in user explorations)
        allParams[EventParam.ScreenName] = previousScreen;

        if (name === EventType.AuthLinkRequested && params.type) {
            // Include resent params for AuthLinkRequested events
            let linkParams = requestedAuthLinks[params.type];
            if (linkParams) {
                linkParams[EventParam.IsResent] = true;
                linkParams[EventParam.Count] += 1;
            } else {
                linkParams = {
                    [EventParam.IsResent]: false,
                    [EventParam.Count]: 1,
                };
            }
            requestedAuthLinks[params.type] = linkParams;
            allParams = { ...allParams, ...linkParams };
        }
        return sanitizeParams(allParams);
    };

    const sanitizeParams = (params) => {
        for (const name in params) {
            const value = params[name];
            if (value === undefined || value === null) {
                // Remove undefined values
                delete params[name];
            } else if (typeof value === 'boolean') {
                // Convert booleans to strings, as raw booleans may not work consistently in Analytics
                params[name] = value.toString();
            } else if (name === EventParam.ItemType) {
                // Ensure item types are consistent with mobile, e.g. 'waypoint'
                params[name] = value.toLowerCase();
            }
        }
        return params;
    };

    const parseEnum = (strValue, enumObject) => {
        if (strValue) {
            const strLower = strValue.toLowerCase();
            for (const key in enumObject) {
                if (strLower.includes(enumObject[key].toLowerCase())) {
                    return enumObject[key];
                }
            }
        }
        return 'unknown';
    };

    const logAnalyticsEvent = (name, params = {}) => {
        try {
            const allParams = getDefaultParams(name, params);
            if (analytics) {
                console.log(`logAnalyticsEvent ${name}`, allParams);
                logEvent(analytics, name, allParams);
            } else {
                console.log(`logAnalyticsEvent ${name} queued: must initialize analytics by calling setAnalyticsUser before you can log an event`);
                pendingEvents.push(logAnalyticsEvent.bind(null, name, allParams));
            }
        } catch (error) {
            console.error(`logAnalyticsEvent failed`, error);
        }
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    /**
     * Returns a standardized map type in the format <Provider>_<Style>, e.g. Google_Satellite
     * This will ensure the 'basemap' property is consistent across all products.
     * Exception: Web is the only client that includes 'Legacy' when applicable (e.g. Google_Satellite_Legacy).
     */
    const getFormattedMapType = (rawProvider, rawStyle, rawEngine) => {
        const provider = capitalizeFirstLetter(parseEnum(rawProvider, MapProvider));
        const style = capitalizeFirstLetter(parseEnum(rawStyle, MapStyle));
        const isLegacy = !rawEngine || rawEngine === 'leaflet';
        return isLegacy ? `${provider}_${style}_Legacy` : `${provider}_${style}`;
    };

    /**
     * getLayerGroup
     * @param {LayerGroup} layerGroup
     * @param {string} layerId
     * @returns {EventLayerGroup}
     */
    const getLayerGroup = (layerGroup, layerId) => {
        // Handle special cases to match mobile
        if (layerGroup === LayerGroup.WMU) {
            return EventLayerGroup.WMU;
        } else if (layerGroup === LayerGroup.LEH) {
            return EventLayerGroup.LEH;
        } else if (layerGroup === LayerGroup.Purchasable || layerGroup === LayerGroup.County || layerId === LayerGroup.County) {
            return EventLayerGroup.LandownerMap;
        } else if (layerGroup === LayerGroup.Subscription) {
            return EventLayerGroup.Subscription;
        } else if (layerGroup === LayerGroup.BuiltInLayer) {
            return EventLayerGroup.BuiltInLayer;
        }
        return null;
    };

    const getResultType = (resultType) => {
        if (resultType === ResultType.BoundaryHidden) {
            return ResultType.Boundary;
        } else if (resultType === ResultType.CollectionHidden || resultType === ResultType.CollectionFiltered) {
            return ResultType.Collection;
        }
        return resultType;
    };

    const getCoordinateFormat = (format) => {
        return typeof format === 'string' ? format : CoordinateFormatKey[format];
    };

    const logAuthSignedIn = (authType) => {
        logAnalyticsEvent(EventType.AuthSignedIn, {
            [EventParam.AuthType]: authType,
        });
    };

    const logAuthSignedOut = () => {
        logAnalyticsEvent(EventType.AuthSignedOut);
    };

    const logAuthAccountCreated = (authType) => {
        logAnalyticsEvent(EventType.AuthAccountCreated, {
            [EventParam.AuthType]: authType,
        });
    };

    const logAuthPasswordReset = () => {
        logAnalyticsEvent(EventType.AuthPasswordReset);
    };

    const logAuthLinkRequested = (linkType) => {
        logAnalyticsEvent(EventType.AuthLinkRequested, {
            [EventParam.AuthType]: linkType,
        });
    };

    const logBasemapSwitched = (mapProvider, mapStyle, engine) => {
        const mapType = getFormattedMapType(mapProvider, mapStyle, engine);

        if (userBasemap && userBasemap !== mapType) {
            logAnalyticsEvent(EventType.BasemapSwitched, {
                [EventParam.Source]: userBasemap,
                [EventParam.Target]: mapType,
            });
        }

        // Also set the user property so that the map type is included on all future events
        setAnalyticsUserBasemap(mapType);
    };

    const logBasemapModeChanged = (is3dEnabled) => {
        logAnalyticsEvent(EventType.BasemapModeChanged, {
            [EventParam.MapType]: userBasemap,
            [EventParam.Mode]: is3dEnabled ? '3d' : 'default',
        });
    };

    const logChatCleared = (chat) => {
        logAnalyticsEvent(EventType.ChatCleared, getChatParams(chat));
    };

    const logChatDrawerViewed = () => {
        logAnalyticsEvent(EventType.ChatDrawerViewed);
    };

    const logChatLeft = (chat) => {
        logAnalyticsEvent(EventType.ChatLeft, getChatParams(chat));
    };

    const logChatSelected = (chat) => {
        logAnalyticsEvent(EventType.ChatSelected, getChatParams(chat));
    };

    const logChatMessageSent = (chat) => {
        logAnalyticsEvent(EventType.ChatMessageSent, getChatParams(chat));
    };

    const logContactGroupCreated = (contactGroup) => {
        logAnalyticsEvent(EventType.ContactGroupCreated, getContactGroupParams(contactGroup));
    };

    const logContactGroupDeleted = (contactGroup) => {
        logAnalyticsEvent(EventType.ContactGroupDeleted, getContactGroupParams(contactGroup));
    };

    const logContactGroupEdited = (contactGroup) => {
        logAnalyticsEvent(EventType.ContactGroupEdited, getContactGroupParams(contactGroup));
    };

    const logContactGroupSettingsViewed = (contactGroup) => {
        logAnalyticsEvent(EventType.ContactGroupSettingsViewed, getContactGroupParams(contactGroup));
    };

    /**
     * Logged when the Collection Filters view opens.
     * @param {EventSource} source - where it was opened from: Drawer (collection menu) or GlobalSearch (hidden results)
     * @param {Object} filter - the saved filter, see getCollectionFilterContentType
     * @param {number} hiddenItemCount - number of items the saved filter hides
     */
    const logCollectionFilterEditStarted = (source, filter, hiddenItemCount) => {
        logAnalyticsEvent(EventType.CollectionFilterEditStarted, {
            [EventParam.Source]: source,
            [EventParam.ContentType]: getCollectionFilterContentType(filter),
            [EventParam.ItemCount]: hiddenItemCount,
        });
    };

    /**
     * Logged when a collection filter is applied.
     * @param {EventSource} source - where the Collection Filters view was opened from
     * @param {EventMethod} method - Reset if the applied filter is the default (hides nothing), otherwise Apply
     * @param {Object} filter - the applied filter, see getCollectionFilterContentType
     * @param {number} hiddenItemCount - number of items the applied filter hides
     */
    const logCollectionFilterEdited = (source, method, filter, hiddenItemCount) => {
        logAnalyticsEvent(EventType.CollectionFilterEdited, {
            [EventParam.Source]: source,
            [EventParam.Method]: method,
            [EventParam.ContentType]: getCollectionFilterContentType(filter),
            [EventParam.Mode]: filter?.dateMode,
            [EventParam.Status]: filter?.status,
            [EventParam.ItemCount]: hiddenItemCount,
        });
    };

    /**
     * Returns the parts of a collection filter that hide something, as tags in a fixed order, e.g. 'date,track,icon',
     * or 'none'. The type tags (e.g. 'track') mean at least one colour of that type is hidden.
     * @param {Object} filter
     * @param {EventDateMode} filter.dateMode
     * @param {Array<CollectionType>} filter.typesWithHiddenColours
     * @param {boolean} filter.hasHiddenIcons
     * @param {EventStatus} [filter.status] - HiddenTypeColours, HiddenColours or HiddenTypes, if any colours are hidden
     */
    const getCollectionFilterContentType = (filter) => {
        const tags = [];
        if (filter?.dateMode && filter.dateMode !== EventDateMode.AllDates) {
            tags.push(EventFilterContent.Date);
        }
        for (const type of CollectionType.All) {
            if (filter?.typesWithHiddenColours?.includes(type)) {
                tags.push(type.toLowerCase());
            }
        }
        if (filter?.hasHiddenIcons) {
            tags.push(EventFilterContent.Icon);
        }
        return tags.length ? tags.join(',') : EventFilterContent.None;
    };

    const getChatParams = (chat) => {
        if (chat?.type) {
            return {
                [EventParam.ChatType]: chat.type,
                ...getContactGroupParams(chat),
            };
        } else {
            return null;
        }
    };

    const getContactGroupParams = (contactGroup) => {
        if (contactGroup?.isgroupchat) {
            const role = contactGroup.adminUid ? (contactGroup.isAdmin ? 'admin' : 'participant') : undefined;
            const participantCount = Object.values(contactGroup.participants).filter((isActive) => isActive).length;
            const hasCustomName = contactGroup.chatname && !contactGroup.chatname.toLowerCase().startsWith('new group');
            return {
                [EventParam.Role]: role,
                [EventParam.HasCustomName]: hasCustomName,
                [EventParam.HasCustomImage]: Boolean(contactGroup.profilePicDate),
                [EventParam.ParticipantCount]: participantCount,
            };
        } else {
            return {};
        }
    };

    const logDialogOpened = (dialogName, type) => {
        const screenName = type ? `${dialogName}_${type}` : dialogName;
        logScreenView(`${screenName}_dialog`);
    };

    const logDialogClosed = () => {
        logScreenView(dialogScreen);
    };

    const logHomeScreenViewed = () => {
        logScreenView('home');
    };

    const logInviteRequestAccepted = (source) => {
        logAnalyticsEvent(EventType.InviteRequestAccepted, {
            [EventParam.Source]: source,
        });
    };

    const logInviteRequestRejected = (source) => {
        logAnalyticsEvent(EventType.InviteRequestRejected, {
            [EventParam.Source]: source,
        });
    };

    const logInviteRequestRevoked = () => {
        logAnalyticsEvent(EventType.InviteRequestRevoked);
    };

    const logInviteRequestSent = (isResent) => {
        logAnalyticsEvent(EventType.InviteRequestSent, {
            [EventParam.IsResent]: isResent,
        });
    };

    const logItemCreated = (itemType, pinBackgroundColor, pinOverlayName, coordinateFormat, coordinateLocation, isVisible) => {
        logAnalyticsEvent(EventType.ItemCreated, {
            [EventParam.ItemType]: itemType,
            [EventParam.ItemPinColor]: pinBackgroundColor,
            [EventParam.ItemPinOveray]: pinOverlayName,
            [EventParam.CoordinateFormat]: getCoordinateFormat(coordinateFormat),
            [EventParam.CoordinateLocation]: coordinateLocation,
            [EventParam.IsVisible]: isVisible,
        });
    };

    const logItemCreateStarted = (itemType) => {
        logAnalyticsEvent(EventType.ItemCreateStarted, {
            [EventParam.ItemType]: itemType,
        });
    };

    const logItemEdited = (itemType, pinBackgroundColor, pinOverlayName, isVisible) => {
        logAnalyticsEvent(EventType.ItemEdited, {
            [EventParam.ItemType]: itemType,
            [EventParam.ItemPinColor]: pinBackgroundColor,
            [EventParam.ItemPinOveray]: pinOverlayName,
            [EventParam.IsVisible]: isVisible,
        });
    };

    const logItemEditStarted = (itemType) => {
        logAnalyticsEvent(EventType.ItemEditStarted, {
            [EventParam.ItemType]: itemType,
        });
    };

    const logItemDeleted = (source, itemType) => {
        logAnalyticsEvent(EventType.ItemDeleted, {
            [EventParam.Source]: source,
            [EventParam.ItemType]: itemType,
            [EventParam.ItemCount]: 1,
        });
    };

    const logItemDeleteStarted = (source, itemType) => {
        logAnalyticsEvent(EventType.ItemDeleteStarted, {
            [EventParam.Source]: source,
            [EventParam.ItemType]: itemType,
        });
    };

    /**
     * Logged when received shared items are added to the collection.
     * @param {Object} chat - the chat the items were shared in
     * @param {Array} items - the items that were added
     * @param {EventMethod} method - how the items were received, e.g. Clipboard
     * @param {EventStatus} status - how duplicates were resolved: NoDuplicates, DuplicatesAccepted or DuplicatesSkipped
     */
    const logItemReceived = (chat, items, method, status) => {
        logAnalyticsEvent(EventType.ItemReceived, {
            [EventParam.Method]: method,
            [EventParam.Status]: status,
            [EventParam.ItemType]: getMajorityItemTypeParam(items),
            [EventParam.ItemCount]: items?.length || 1,
            ...getChatParams(chat),
        });
    };

    /**
     * Logged when the user starts adding received shared items to the collection.
     * @param {Object} chat - the chat the items were shared in
     * @param {Array} items - the items that were received
     * @param {EventMethod} method - how the items were received, e.g. Clipboard
     * @param {EventStatus} status - whether any items are duplicates: NoDuplicates, SomeDuplicates or AllDuplicates
     */
    const logItemReceiveStarted = (chat, items, method, status) => {
        logAnalyticsEvent(EventType.ItemReceiveStarted, {
            [EventParam.Method]: method,
            [EventParam.Status]: status,
            [EventParam.ItemType]: getMajorityItemTypeParam(items),
            [EventParam.ItemCount]: items?.length || 1,
            ...getChatParams(chat),
        });
    };

    const logItemSelected = (source, itemType, pinBackgroundColor, pinOverlayName, isVisible) => {
        logAnalyticsEvent(EventType.ItemSelected, {
            [EventParam.Source]: source,
            [EventParam.ItemType]: itemType,
            [EventParam.ItemPinColor]: pinBackgroundColor,
            [EventParam.ItemPinOveray]: pinOverlayName,
            [EventParam.IsVisible]: isVisible,
        });
    };

    /**
     * Logged when items are shared.
     * @param {EventSource} source - where the share was started, e.g. Chat or ItemDetails
     * @param {Array} items - the items that were shared
     * @param {EventMethod} method - how the items were shared, e.g. Clipboard or Sms (mobile only)
     */
    const logItemShared = (source, items, method) => {
        logAnalyticsEvent(EventType.ItemShared, {
            [EventParam.Source]: source,
            [EventParam.Method]: method,
            [EventParam.ItemType]: getMajorityItemTypeParam(items),
            [EventParam.ItemCount]: items?.length || 1,
        });
    };

    const logItemShareStarted = (source) => {
        logAnalyticsEvent(EventType.ItemShareStarted, {
            [EventParam.Source]: source,
        });
    };

    const getMajorityItemTypeParam = (items) => {
        if (!Array.isArray(items) || items.length === 0) {
            return null;
        }

        const count = (type) => items.filter((item) => item.type === type).length;
        const waypointCount = count(CollectionType.Waypoint);
        const trackCount = count(CollectionType.Track);
        const drawingCount = count(CollectionType.Drawing);

        if (waypointCount >= trackCount && waypointCount >= drawingCount) return CollectionType.Waypoint;
        if (trackCount >= waypointCount && trackCount >= drawingCount) return CollectionType.Track;
        return CollectionType.Drawing;
    };

    const logLayerSelected = (layerGroup, layerId, isVisible) => {
        logAnalyticsEvent(EventType.LayerSelected, {
            [EventParam.LayerGroup]: getLayerGroup(layerGroup, layerId),
            [EventParam.IsVisible]: isVisible,
        });
    };

    const logLandownerMapSelected = (source, mapName, purchaseStatus, isMapVisible, isBoundaryVisible) => {
        logAnalyticsEvent(EventType.LandownerMapSelected, {
            [EventParam.Source]: source,
            [EventParam.MapName]: mapName,
            [EventParam.PurchaseStatus]: purchaseStatus,
            [EventParam.IsVisible]: isMapVisible,
            [EventParam.IsBoundaryVisible]: isBoundaryVisible,
        });
    };

    const logLayerBoundarySelected = (source, layerGroup, layerId, isVisible) => {
        logAnalyticsEvent(EventType.LayerBoundarySelected, {
            [EventParam.Source]: source,
            [EventParam.LayerGroup]: getLayerGroup(layerGroup, layerId),
            [EventParam.IsVisible]: isVisible,
        });
    };

    /**
     * The 'search' event is logged for both global search as well as list-based (drawer filter) search.
     * List-based search events may optionally include the ResultType and ResultSubtype params.
     */
    const logSearch = (params) => {
        const layerGroup = params?.[EventParam.LayerGroup];
        if (layerGroup === LayerGroup.County || layerGroup === LayerGroup.Purchasable) {
            logAnalyticsEvent(EventType.Search, {
                [EventParam.Source]: params?.[EventParam.Source],
                [EventParam.ResultType]: EventLayerGroup.LandownerMap,
            });
        } else if (layerGroup) {
            logAnalyticsEvent(EventType.Search, {
                [EventParam.Source]: params?.[EventParam.Source],
                [EventParam.ResultType]: ResultType.Boundary,
                [EventParam.ResultSubtype]: getLayerGroup(layerGroup),
            });
        } else {
            logAnalyticsEvent(EventType.Search, {
                [EventParam.Source]: params?.[EventParam.Source],
                [EventParam.ResultType]: getResultType(params?.[EventParam.ResultType]),
                [EventParam.ResultSubtype]: params?.[EventParam.ResultSubtype],
            });
        }
    };

    const logSearchResultSelected = (type, subtype, isVisible) => {
        logAnalyticsEvent(EventType.SearchResultSelected, {
            [EventParam.ResultType]: getResultType(type),
            [EventParam.ResultSubtype]: subtype,
            [EventParam.IsVisible]: isVisible,
        });
    };

    const logBoundarySearchResultSelected = (type, layerGroup, layerId, isVisible) => {
        logAnalyticsEvent(EventType.SearchResultSelected, {
            [EventParam.ResultType]: getResultType(type),
            [EventParam.ResultSubtype]: getLayerGroup(layerGroup, layerId),
            [EventParam.IsVisible]: isVisible,
        });
    };

    const logLandownerMapSearchResultSelected = (mapName, purchaseStatus, isMapVisible, isBoundaryVisible) => {
        logAnalyticsEvent(EventType.SearchResultSelected, {
            [EventParam.ResultType]: EventLayerGroup.LandownerMap,
            [EventParam.ResultSubtype]: mapName,
            [EventParam.PurchaseStatus]: purchaseStatus,
            [EventParam.IsVisible]: isMapVisible,
            [EventParam.IsBoundaryVisible]: isBoundaryVisible,
        });
    };

    const logItemLocationJumpedTo = (source, itemType) => {
        logAnalyticsEvent(EventType.ItemLocationJumpedTo, {
            [EventParam.Source]: source,
            [EventParam.ItemType]: itemType,
        });
    };

    const logBoundaryLocationJumpedTo = (source, layerGroup, layerId) => {
        logAnalyticsEvent(EventType.BoundaryLocationJumpedTo, {
            [EventParam.Source]: source,
            [EventParam.LayerGroup]: getLayerGroup(layerGroup, layerId),
        });
    };

    const logBoundaryResourceLinkOpened = (resourceType, layerGroup, layerId) => {
        logAnalyticsEvent(EventType.ResourceLinkOpened, {
            [EventParam.ResourceType]: resourceType,
            [EventParam.Source]: EventSource.BoundaryDetails,
            [EventParam.LayerGroup]: getLayerGroup(layerGroup, layerId),
        });
    };

    const logBoundaryResourcePreviewed = (resourceType, layerGroup, layerId) => {
        logAnalyticsEvent(EventType.ResourcePreviewed, {
            [EventParam.ResourceType]: resourceType,
            [EventParam.Source]: EventSource.BoundaryDetails,
            [EventParam.LayerGroup]: getLayerGroup(layerGroup, layerId),
        });
    };

    const logAgLeaseEmailed = () => {
        logAnalyticsEvent(EventType.AgLeaseEmailed);
    };

    const logAgLeaseViewed = () => {
        logAnalyticsEvent(EventType.AgLeaseViewed);
    };

    const logNavigatedTo = (target) => {
        const source = product.value.name;
        logAnalyticsEvent(EventType.NavigatedTo, {
            [EventParam.Source]: source,
            [EventParam.Target]: target,
        });
    };

    const logViewItem = (sku) => {
        logAnalyticsEvent(EventType.ViewItem, getPurchaseParams([sku]));
    };

    const logAddToCart = (sku) => {
        logAnalyticsEvent(EventType.AddToCart, getPurchaseParams([sku]));
    };

    const logRemoveFromCart = (sku) => {
        logAnalyticsEvent(EventType.RemoveFromCart, getPurchaseParams([sku]));
    };

    const logViewCart = (skus) => {
        logAnalyticsEvent(EventType.ViewCart, getPurchaseParams(skus));
    };

    const logBeginCheckout = (skus, coupon) => {
        logAnalyticsEvent(EventType.BeginCheckout, { coupon, ...getPurchaseParams(skus) });
    };

    const getPurchaseParams = (skus) => {
        const items = [];
        let total = 0;

        for (const sku of skus) {
            try {
                const { productPrice, originalPrice, productName, isSubscription } = useStoreProduct(sku);
                const price = parseFloat(productPrice.value);
                const discount = originalPrice.value - price;
                const item_category = isSubscription.value ? 'subscription' : 'map'; // Use the same item_category that will be set by the Billing API's reportPurchase()
                items.push({ item_id: sku, item_name: productName.value, discount, price, quantity: 1, item_category });
                total += price;
            } catch (error) {
                console.error(`Failed to get analytics metadata for store item ${sku}`, error);
            }
        }

        return {
            currency: 'CAD',
            value: total,
            items: items,
            origin: product.value.name,
        };
    };

    // Manually log the built-in screen view event to track view changes
    // https://firebase.google.com/docs/analytics/screenviews#manually_track_screens
    // https://support.google.com/analytics/answer/9234069?hl=en
    const logScreenView = (screenName) => {
        if (previousScreen === screenName) {
            return;
        }

        logAnalyticsEvent(EventType.ScreenView, {
            [FirebaseEventParam.Screen]: screenName,
            [FirebaseEventParam.Class]: window.location.pathname,
            [FirebaseEventParam.PreviousScreen]: previousScreen,
            [FirebaseEventParam.PreviousClass]: previousClass,
        });

        previousScreen = screenName;
        previousClass = window.location.pathname;
    };

    const logSupportEmailed = () => {
        logAnalyticsEvent(EventType.SupportEmailed);
    };

    const logShopInStoreClicked = () => {
        logAnalyticsEvent(EventType.ShopInStoreClicked);
    };

    const logAppRegionChanged = (source, target) => {
        logAnalyticsEvent(EventType.AppRegionChanged, {
            [EventParam.Source]: getAppRegion(source),
            [EventParam.Target]: getAppRegion(target),
        });

        // Also set the user property so that the region is included on all future events
        setAnalyticsUserAppRegion(target);
    };

    const getAppRegion = (region) => {
        // Explicitly set to 'canada' if empty/null for clarity in reports, otherwise it will show up as '(not set)'
        return region || 'canada';
    };

    const logBroadcastLocationViewed = (method) => {
        logAnalyticsEvent(EventType.ContactLocationViewed, {
            [EventParam.ContentType]: EventContentType.Broadcast,
            [EventParam.Method]: method,
        });
    };

    const logCurrentLocationViewed = () => {
        logAnalyticsEvent(EventType.ContactLocationViewed, {
            [EventParam.ContentType]: EventContentType.CurrentLocation,
        });
    };

    return {
        setAnalyticsUser,
        setAnalyticsUserAppRegion,
        setAnalyticsUserBasemap,
        setAnalyticsIsFreeUser,
        setAnalyticsUserSubscriptionTier,
        setAnalyticsUserCollectionSize,
        setAnalyticsUserChatSize,
        setAnalyticsUserHasHiddenItems,
        setAnalyticsUserLanguage,
        setAnalyticsUserCoordinateFormat,

        logAnalyticsEvent,

        logAddToCart,
        logAgLeaseEmailed,
        logAgLeaseViewed,
        logAppRegionChanged,
        logAuthAccountCreated,
        logAuthLinkRequested,
        logAuthPasswordReset,
        logAuthSignedIn,
        logAuthSignedOut,
        logBasemapModeChanged,
        logBasemapSwitched,
        logBeginCheckout,
        logBoundaryLocationJumpedTo,
        logBoundaryResourceLinkOpened,
        logBoundaryResourcePreviewed,
        logBoundarySearchResultSelected,
        logBroadcastLocationViewed,
        logChatCleared,
        logChatDrawerViewed,
        logChatLeft,
        logChatMessageSent,
        logChatSelected,
        logCollectionFilterEdited,
        logCollectionFilterEditStarted,
        logContactGroupCreated,
        logContactGroupDeleted,
        logContactGroupEdited,
        logContactGroupSettingsViewed,
        logCurrentLocationViewed,
        logDialogClosed,
        logDialogOpened,
        logHomeScreenViewed,
        logInviteRequestAccepted,
        logInviteRequestRejected,
        logInviteRequestRevoked,
        logInviteRequestSent,
        logItemCreated,
        logItemCreateStarted,
        logItemDeleted,
        logItemDeleteStarted,
        logItemEdited,
        logItemEditStarted,
        logItemReceived,
        logItemReceiveStarted,
        logItemSelected,
        logItemShared,
        logItemShareStarted,
        logItemLocationJumpedTo,
        logLandownerMapSelected,
        logLandownerMapSearchResultSelected,
        logLayerSelected,
        logLayerBoundarySelected,
        logNavigatedTo,
        logRemoveFromCart,
        logSearch,
        logSearchResultSelected,
        logScreenView,
        logShopInStoreClicked,
        logSupportEmailed,
        logViewCart,
        logViewItem,

        lookupAnalyticsIds,

        EventParam,
        EventType,
        EventSource,
        EventMethod,
        EventContentType,
        EventStatus,
        EventDateMode,
    };
}
